import { Badge } from "@/design-system/components/ui";
import type { BadgeProps } from "@/design-system/components/ui/Badge";
import { useRoleLabels } from "@/hooks/useRoles";
import { cn } from "@/design-system/lib/utils";

/**
 * Admin-managed Lookups v1.0 §4d — renders a role code with its admin-
 * editable display name. Resolves the label via <see cref="useRoleLabels"/>
 * from <c>@/hooks/useRoles</c>, which fetches the canonical
 * <c>GET /api/roles</c>. Renaming a role on <c>/admin/roles</c>
 * propagates here within seconds (admin save invalidates the
 * <c>['roles']</c> query key).
 *
 * Pre-PR-2 this component fetched <c>rolesApi.matrix()</c> against the
 * legacy permission-matrix endpoint that occupied <c>/api/roles</c>;
 * the matrix endpoint now lives at <c>/api/roles/matrix</c> and
 * <c>RoleChip</c> consumes the lighter active-roles list instead.
 */
interface RoleChipProps {
  code: string;
  /** Optional explicit title override (e.g. when the caller already has it). */
  title?: string | null;
  status?: BadgeProps["status"];
  className?: string;
}

export function RoleChip({ code, title, status = "gold", className }: RoleChipProps) {
  const resolvedTitle = useRoleTitle(code, title);
  const display = resolvedTitle ? `${code} · ${resolvedTitle}` : code;
  const tooltip = resolvedTitle ? `${code} — ${resolvedTitle}` : code;
  return (
    <Badge
      status={status}
      title={tooltip}
      className={cn("max-w-[14rem] overflow-hidden", className)}
    >
      <span className="truncate">{display}</span>
    </Badge>
  );
}

function useRoleTitle(code: string, override?: string | null): string | undefined {
  const { titleOf } = useRoleLabels();
  if (override) return override;
  return titleOf(code);
}
